import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { nativePlayerBridge, nativePlayerCapabilities } from "@hybrid-iptv/player";
import { useActiveProviderSourceId } from "../epg/api.js";
import { healthCycleNow, invalidateCatalogQueries, refreshCatalogNow, retestChannelNow } from "../library/syncActions.js";
import { SYNC_FREQUENCY_LABELS, useSyncSettings, type SyncFrequency } from "../library/syncSettingsStore.js";
import { useNotifications } from "../library/notificationsStore.js";
import { nativeAlertsAvailable, requestNativeAlerts } from "../library/nativeAlerts.js";
import { useChannels } from "../epg/hooks.js";
import { useChannelHealthStore } from "../library/channelHealthStore.js";
import { ChannelStatusDialog } from "../epg/ChannelStatusDialog.js";
import { RemoteDebugCard } from "../settings/RemoteDebugCard.js";
import { AppUpdateCard } from "../settings/AppUpdateCard.js";
import { PhoneRemoteCard } from "../settings/PhoneRemoteCard.js";
import { YoutubeAccountCard } from "../settings/YoutubeAccountCard.js";
import { TimeshiftSettings } from "../settings/TimeshiftSettings.js";
import { useAppearance } from "../library/appearanceStore.js";
import { useTvKeyboard, type BuiltInKeyboard } from "../components/tvKeyboardStore.js";
import { EntitlementCard } from "../settings/EntitlementCard.js";
import { usePlayerSettings } from "../player/playerSettingsStore.js";
import { runsOnDevice } from "../api/client.js";
import { brand } from "../brand.js";
import { useProfiles } from "../settings/profileStore.js";
import { startScreenFor, useStartScreen } from "../settings/startScreenStore.js";
import { SLEEP_CHOICES, sleepLengthLong, useSleep, type IdleHours } from "../player/sleepTimer.js";
import { AutostartRow } from "../settings/AutostartRow.js";
import { CountryCard } from "../settings/CountryCard.js";
import { useLauncher } from "../launcher/launcherStore.js";
import { launcherAvailable } from "../launcher/launcherApi.js";
import { ThisBoxCard } from "../launcher/ThisBoxCard.js";

/** Audio delay choices, ms (G7): 50 ms steps to ±200, then 100 ms to ±500. 0 is the default. */
const AUDIO_DELAY_CHOICES = [-500, -400, -300, -200, -150, -100, -50, 0, 50, 100, 150, 200, 300, 400, 500];

const SETTINGS_LINKS = [
  {
    to: "/settings/source",
    label: "Source",
    desc: "Connect your streaming source — Xtream Codes or M3U playlist.",
    icon: "M4 7h16M4 11h16M4 15h12M4 19h8",
    badge: null as string | null,
  },
  {
    to: "/settings/profiles",
    label: "Profiles",
    desc: "Up to 5 viewers, each with their own favourites and continue-watching.",
    icon: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 20a8 8 0 0 1 16 0",
    badge: null as string | null,
  },
  {
    to: "/settings/guide",
    label: "Guide",
    desc: "Layout, transparency, text size and how many hours the grid shows.",
    icon: "M3 5h18v14H3zM9 5v14M3 10h18",
    badge: null as string | null,
  },
  {
    to: "/settings/parental",
    label: "Parental Controls",
    desc: "Hide categories behind a PIN so they do not appear anywhere in the app.",
    icon: "M12 3a4 4 0 0 1 4 4v3H8V7a4 4 0 0 1 4-4zM6 10h12v10H6z",
    badge: null as string | null,
  },
  {
    to: "/settings/remote",
    label: "Remote control",
    desc: "Choose what each remote button does, for a press and a long press.",
    icon: "M15 6l3 3-9 9H6v-3l9-9M6 18h12",
    badge: null as string | null,
  },
  {
    to: "/settings/help",
    label: "Help",
    desc: "Every button and screen in the app, in plain words — the remote, the player, recording and more.",
    icon: "M12 19h.01M9.2 9.2a3 3 0 1 1 4.3 2.9c-.9.5-1.5 1.2-1.5 2.2v.4M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z",
    badge: null as string | null,
  },
  {
    to: "/settings/about",
    label: "About",
    desc: "The app and the credits for the data it shows.",
    icon: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM12 11v6M12 7h.01",
    badge: null as string | null,
  },
];

const FREQUENCIES: SyncFrequency[] = ["off", "12h", "24h", "36h"];

function formatUpdated(iso: string | null): string {
  if (!iso) return "Never";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "Never" : date.toLocaleString();
}

export function SettingsPage() {
  // --- Sync & Health (Section 12) ---
  const profile = useProfiles((s) => s.profiles.find((p) => p.id === s.activeProfileId));
  const startScreenByProfile = useStartScreen((s) => s.byProfile);
  const setStartScreen = useStartScreen((s) => s.setStartScreen);
  const sleepEndsAt = useSleep((s) => s.endsAt);
  const setSleep = useSleep((s) => s.setSleep);
  const idleHours = useSleep((s) => s.idleHours);
  const setIdleHours = useSleep((s) => s.setIdleHours);
  const heroTrailer = useAppearance((s) => s.heroTrailer);
  const setHeroTrailer = useAppearance((s) => s.setHeroTrailer);
  const liveNowRow = useAppearance((s) => s.liveNowRow);
  const pipOnLeave = usePlayerSettings((s) => s.pipOnLeave);
  const tunneling = usePlayerSettings((s) => s.tunneling);
  const setTunneling = usePlayerSettings((s) => s.setTunneling);
  const setPipOnLeave = usePlayerSettings((s) => s.setPipOnLeave);
  const launcherEnabled = useLauncher((s) => s.enabled);
  const setLauncherEnabled = useLauncher((s) => s.setEnabled);
  const frameRateMatch = usePlayerSettings((s) => s.frameRateMatch);
  const setFrameRateMatch = usePlayerSettings((s) => s.setFrameRateMatch);
  const audioDelayMs = usePlayerSettings((s) => s.audioDelayMs);
  const setAudioDelayMs = usePlayerSettings((s) => s.setAudioDelayMs);
  // Frame-rate matching is offered only where the screen has another refresh rate to switch to
  // (the projector has one: 720p at 60). Unknown until the app answers, so hidden until then.
  const [frameRateSwitching, setFrameRateSwitching] = useState(false);
  useEffect(() => {
    const bridge = nativePlayerBridge();
    if (!bridge) return;
    let live = true;
    void nativePlayerCapabilities(bridge).then((caps) => {
      if (live) setFrameRateSwitching(caps.frameRateSwitching);
    });
    return () => {
      live = false;
    };
  }, []);
  const setLiveNowRow = useAppearance((s) => s.setLiveNowRow);
  const heroRotation = useAppearance((s) => s.heroRotation);
  const setHeroRotation = useAppearance((s) => s.setHeroRotation);
  const tilesPerRow = useAppearance((s) => s.tilesPerRow);
  const setTilesPerRow = useAppearance((s) => s.setTilesPerRow);
  const keyboardMode = useTvKeyboard((s) => s.mode);
  const setKeyboardMode = useTvKeyboard((s) => s.setMode);
  const frequency = useSyncSettings((s) => s.frequency);
  const setFrequency = useSyncSettings((s) => s.setFrequency);
  const lastUpdatedAt = useSyncSettings((s) => s.lastUpdatedAt);
  const hideFailing = useSyncSettings((s) => s.hideFailingChannels);
  const setHideFailing = useSyncSettings((s) => s.setHideFailingChannels);
  const [refreshing, setRefreshing] = useState(false);
  const queryClient = useQueryClient();
  const sourceId = useActiveProviderSourceId();

  // The channel-status report used to open from the guide's toolbar. The guide
  // now carries category and channel only (owner 2026-09-17), and this is where
  // the rest of "is my provider healthy" already lives, so it joins it rather
  // than becoming a second half-page of the same subject.
  const { data: channelData } = useChannels(sourceId);
  const statusChannels = useMemo(() => channelData?.channels ?? [], [channelData]);
  const channelHealth = useChannelHealthStore((s) => s.health);
  const [statusOpen, setStatusOpen] = useState(false);
  const healthCounts = useMemo(() => {
    let dead = 0;
    let degraded = 0;
    for (const channel of statusChannels) {
      const state = channelHealth[channel.id]?.state;
      if (state === "dead") dead += 1;
      else if (state === "degraded") degraded += 1;
    }
    return { dead, degraded };
  }, [statusChannels, channelHealth]);

  const handleRefreshNow = useCallback(async () => {
    setRefreshing(true);
    try {
      // Pressed by hand: download again whatever the stored copy's age.
      await refreshCatalogNow(sourceId, true);
      invalidateCatalogQueries(queryClient);
      void healthCycleNow(queryClient, sourceId);
    } finally {
      setRefreshing(false);
    }
  }, [queryClient, sourceId]);

  // --- Notifications (Section 12 steps 6 + 8) ---
  const notifEnabled = useNotifications((s) => s.enabled);
  const setNotifEnabled = useNotifications((s) => s.setEnabled);
  const requestPermission = useNotifications((s) => s.requestPermission);
  // Subscribe to the raw record, sort in a memo: calling trackedList() inside
  // a selector returns a fresh array every snapshot, which loops React's
  // useSyncExternalStore until the app crashes. (Real bug caught in preview.)
  const trackedTitlesMap = useNotifications((s) => s.trackedTitles);
  const trackedTitles = useMemo(
    () => Object.values(trackedTitlesMap).sort((a, b) => a.title.localeCompare(b.title)),
    [trackedTitlesMap],
  );
  const removeTrackedTitle = useNotifications((s) => s.removeTrackedTitle);
  const [notifBusy, setNotifBusy] = useState(false);
  const [notifHint, setNotifHint] = useState<string | null>(null);

  const browserPermission =
    typeof Notification === "undefined" || nativeAlertsAvailable() ? ("unsupported" as const) : Notification.permission;

  const handleToggleNotifications = useCallback(async () => {
    if (notifEnabled) {
      setNotifEnabled(false);
      setNotifHint("Notifications paused. Tracked titles are kept.");
      return;
    }
    setNotifBusy(true);
    setNotifHint(null);
    try {
      // In the app: Android's notifications, not the browser's (owner, 2026-10-09).
      // Alerts always reach the bell 🔔, so they turn on even if Android says no.
      const native = await requestNativeAlerts();
      if (native !== null) {
        setNotifEnabled(true);
        setNotifHint(
          native === "granted"
            ? "On — you'll be alerted when favorited channels' programs start and when tracked titles air."
            : `On — alerts show in the bell 🔔 at the top. Android's notifications are off for ${brand().appName}; allow them in Android's settings to get pop-ups too.`,
        );
        return;
      }
      const result = await requestPermission(); // user gesture → permission prompt
      if (result === "granted") {
        setNotifEnabled(true);
        setNotifHint("On — you'll be alerted when favorited channels' programs start and when tracked titles air.");
      } else if (result === "unsupported") {
        setNotifHint("This browser doesn't support notifications.");
      } else {
        setNotifHint("Permission was denied — allow notifications for SliqueTV in your browser to enable alerts.");
      }
    } finally {
      setNotifBusy(false);
    }
  }, [notifEnabled, requestPermission, setNotifEnabled]);

  return (
    <div className="page page--settings">
      <div className="settings-hub">
        <div className="settings-header">
          <img
            className="settings-logo"
            src={brand().icon}
            alt={brand().appName}
          />
          <h1 className="settings-title">Settings</h1>
          <p className="settings-subtitle">
            Configure your {brand().appName} experience.
          </p>
        </div>

        <div className="settings-links">
          {SETTINGS_LINKS.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className={`settings-link ${!link.to ? "settings-link--disabled" : ""}`}
              aria-disabled={!link.to}
              tabIndex={link.to ? undefined : -1}
              onClick={(e) => {
                if (!link.to) e.preventDefault();
              }}
            >
              <svg
                className="settings-link-icon"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path d={link.icon} />
              </svg>
              <div className="settings-link-body">
                <span className="settings-link-label">
                  {link.label}
                  {link.badge && (
                    <span className="settings-link-badge">{link.badge}</span>
                  )}
                </span>
                <span className="settings-link-desc">{link.desc}</span>
              </div>
              <svg
                className="settings-link-chevron"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path d="m9 5 7 7-7 7" />
              </svg>
            </Link>
          ))}
        </div>

        <div className="settings-card">
          <h2 className="settings-card-title">Home</h2>
          <p className="settings-card-desc">
            What the home screen shows. On a low-powered TV, switching these off
            (and keeping rows short) makes the app quicker to respond.
          </p>
          {runsOnDevice() && profile && (
            <>
              <div className="settings-row">
                <label className="settings-row-label" htmlFor="start-on">
                  Start on
                </label>
                <select
                  id="start-on"
                  className="library-select"
                  value={startScreenFor(profile.id, startScreenByProfile)}
                  onChange={(e) => setStartScreen(profile.id, e.target.value === "tv" ? "tv" : "home")}
                  aria-label={`Where the app opens for ${profile.name}`}
                >
                  <option value="home">Home</option>
                  <option value="tv">Live TV</option>
                </select>
              </div>
              <p className="settings-row-hint">
                For {profile.name}. Live TV opens full screen on your last channel; Back goes to the guide, Back again
                to Home.
              </p>
            </>
          )}
          {/* Only SliqueTV's direct-download build offers it; the row answers null elsewhere. */}
          <AutostartRow />
          <div className="settings-row">
            <label className="settings-row-label" htmlFor="hero-trailer">
              Banner trailer
            </label>
            <input
              id="hero-trailer"
              type="checkbox"
              checked={heroTrailer}
              onChange={(e) => setHeroTrailer(e.target.checked)}
              aria-label="Play the featured title's trailer behind the banner"
            />
          </div>
          <p className="settings-row-hint">
            Plays the featured title's trailer behind the banner. Off for speed.
          </p>
          <div className="settings-row">
            <label className="settings-row-label" htmlFor="live-now-row">
              Live Now row
            </label>
            <input
              id="live-now-row"
              type="checkbox"
              checked={liveNowRow}
              onChange={(e) => setLiveNowRow(e.target.checked)}
              aria-label="Show the Live Now row on the home screen"
            />
          </div>
          <p className="settings-row-hint">
            What is on now, matched to your channels. Turn off for more speed.
          </p>
          <div className="settings-row">
            <label className="settings-row-label" htmlFor="hero-rotation">
              Rotating featured title
            </label>
            <input
              id="hero-rotation"
              type="checkbox"
              checked={heroRotation}
              onChange={(e) => setHeroRotation(e.target.checked)}
              aria-label="Change the featured title every minute"
            />
          </div>
          <p className="settings-row-hint">
            The banner changes title every minute. Turn off for more speed.
          </p>
          <div className="settings-row">
            <label className="settings-row-label" htmlFor="tiles-per-row">
              Titles per row
            </label>
            <select
              id="tiles-per-row"
              className="library-select"
              value={tilesPerRow}
              onChange={(e) => setTilesPerRow(Number(e.target.value) === 24 ? 24 : 12)}
              aria-label="How many titles each row shows"
            >
              <option value={12}>12</option>
              <option value={24}>24</option>
            </select>
          </div>
          <p className="settings-row-hint">
            Longer rows give more to browse; shorter rows draw faster.
          </p>
        </div>

        <CountryCard />

        <div className="settings-card">
          <h2 className="settings-card-title">Text entry</h2>
          <p className="settings-card-desc">
            A keyboard built for a remote: every key is a button, so the arrow keys reach it.
            The app shows it by itself if your device's keyboard won't type — this is where to
            always use it, or turn it off.
          </p>
          <div className="settings-row">
            <label className="settings-row-label" htmlFor="built-in-keyboard">
              App keyboard
            </label>
            <select
              id="built-in-keyboard"
              className="library-select"
              value={keyboardMode}
              onChange={(e) => setKeyboardMode(e.target.value as BuiltInKeyboard)}
              aria-label="When to show the app's own on-screen keyboard"
            >
              <option value="auto">When yours won't work</option>
              <option value="always">Always</option>
              <option value="never">Never</option>
            </select>
          </div>
          <p className="settings-row-hint">
            Always gives you a keyboard the arrow keys can always type on. Never leaves typing
            entirely to your device.
          </p>
        </div>

        {/* After Text entry on purpose: the Settings audit walks down to Text entry, so the map
            above stays as recorded (roadmap Section 32, L1). */}
        {launcherAvailable() && (
          <div className="settings-card">
            <h2 className="settings-card-title">Launcher mode</h2>
            <div className="settings-row">
              <label className="settings-row-label" htmlFor="launcher-mode">
                Use Home as the TV&apos;s home screen
              </label>
              <input
                id="launcher-mode"
                type="checkbox"
                checked={launcherEnabled}
                onChange={(e) => setLauncherEnabled(e.target.checked)}
                aria-label="Show your TV's apps, Quick settings, Android settings and inputs at the top of Home"
              />
            </div>
            <p className="settings-row-hint">
              Home starts with every app on this box, Quick settings, Android settings, the Play
              Store and anything plugged in. Off, Home is as it always was.
            </p>
          </div>
        )}

        {runsOnDevice() && (
          <div className="settings-card">
            <h2 className="settings-card-title">Player</h2>
            <div className="settings-row">
              <label className="settings-row-label" htmlFor="pip-on-leave">
                Picture-in-picture when leaving the app
              </label>
              <input
                id="pip-on-leave"
                type="checkbox"
                checked={pipOnLeave}
                onChange={(e) => setPipOnLeave(e.target.checked)}
                aria-label="Keep a playing video in a small window when you leave the app"
              />
            </div>
            <p className="settings-row-hint">
              On: a video that is playing keeps going in a small window when you leave the app.
              Off: leaving the app stops it. The player's picture-in-picture button works either
              way, on devices that support it.
            </p>
            <div className="settings-row">
              <label className="settings-row-label" htmlFor="tunneling">
                Smooth HD playback (tunnelled video)
              </label>
              <input
                id="tunneling"
                type="checkbox"
                checked={tunneling}
                onChange={(e) => setTunneling(e.target.checked)}
                aria-label="Let the TV chip play video directly, for smoother HD on some devices"
              />
            </div>
            <p className="settings-row-hint">
              Lets your TV's chip play video directly. It can make HD channels (especially 1080i)
              smooth on devices that stutter. If a channel then shows no picture or sound, turn it
              off.
            </p>
            {frameRateSwitching && (
              <>
                <div className="settings-row">
                  <label className="settings-row-label" htmlFor="frame-rate-match">
                    Match the video's frame rate
                  </label>
                  <select
                    id="frame-rate-match"
                    className="library-select"
                    value={frameRateMatch}
                    onChange={(e) => setFrameRateMatch(e.target.value as typeof frameRateMatch)}
                    aria-label="Switch the TV to the video's frame rate"
                  >
                    <option value="off">Off</option>
                    <option value="vod">Films and series</option>
                    <option value="all">Everything</option>
                  </select>
                </div>
                <p className="settings-row-hint">
                  Switches your TV to the video&apos;s frame rate, for smoother films. The screen goes
                  black for a moment when it switches.
                </p>
              </>
            )}
            <div className="settings-row">
              <label className="settings-row-label" htmlFor="audio-delay">
                Audio delay
              </label>
              <select
                id="audio-delay"
                className="library-select"
                value={String(audioDelayMs)}
                onChange={(e) => setAudioDelayMs(Number(e.target.value))}
                aria-label="Move the sound earlier or later than the picture"
              >
                {AUDIO_DELAY_CHOICES.map((ms) => (
                  <option key={ms} value={String(ms)}>
                    {ms === 0 ? "None" : ms > 0 ? `Sound ${ms} ms later` : `Sound ${-ms} ms earlier`}
                  </option>
                ))}
              </select>
            </div>
            <p className="settings-row-hint">
              For a TV or soundbar whose sound is out of step with the picture. Leave it at None
              unless voices and lips don&apos;t match.
            </p>
            <TimeshiftSettings />
            <div className="settings-row">
              <label className="settings-row-label" htmlFor="sleep-timer">
                Sleep timer
              </label>
              <select
                id="sleep-timer"
                className="library-select"
                value={sleepEndsAt === null ? "0" : "running"}
                onChange={(e) => setSleep(Number(e.target.value) || null)}
                aria-label="Stop playback after a set time"
              >
                <option value="0">Off</option>
                {sleepEndsAt !== null && (
                  <option value="running" disabled>
                    Stops in {sleepLengthLong(Math.max(1, Math.round((sleepEndsAt - Date.now()) / 60_000)))}
                  </option>
                )}
                {SLEEP_CHOICES.map((minutes) => (
                  <option key={minutes} value={minutes}>
                    {sleepLengthLong(minutes)}
                  </option>
                ))}
              </select>
            </div>
            <p className="settings-row-hint">
              Stops playback after this long, with a warning a minute before. It is also in the
              player's menu, and it is kept if the app restarts.
            </p>
            <div className="settings-row">
              <label className="settings-row-label" htmlFor="idle-hours">
                Ask if I'm still watching
              </label>
              <select
                id="idle-hours"
                className="library-select"
                value={idleHours}
                onChange={(e) => setIdleHours(Number(e.target.value) as IdleHours)}
                aria-label="Ask if I'm still watching"
              >
                <option value={0}>Off</option>
                <option value={2}>After 2 hours</option>
                <option value={4}>After 4 hours</option>
              </select>
            </div>
            <p className="settings-row-hint">
              After this long with no button pressed, the app asks, and stops if nobody answers. Frees
              your provider connection if the TV is left on.
            </p>
          </div>
        )}

        <div className="settings-card">
          <h2 className="settings-card-title">Sync &amp; Health</h2>
          <p className="settings-card-desc">
            Guide, movie, and series data refresh in the background while the
            app is open. A channel that fails three separate tries is hidden
            and tested again at every update; it comes back when it answers.
            Channels you hide stay hidden.
          </p>
          <div className="settings-row">
            <label className="settings-row-label" htmlFor="sync-frequency">
              Refresh frequency
            </label>
            <select
              id="sync-frequency"
              className="library-select"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as SyncFrequency)}
              aria-label="Background refresh frequency"
            >
              {FREQUENCIES.map((f) => (
                <option key={f} value={f}>{SYNC_FREQUENCY_LABELS[f]}</option>
              ))}
            </select>
          </div>
          <div className="settings-row">
            <span className="settings-row-label">Last updated</span>
            <span className="settings-row-value">{formatUpdated(lastUpdatedAt)}</span>
          </div>
          {/* Labelled like the rows above, so the button sits in the controls' column,
              straight under the frequency list: Down from it lands here (owner, 2026-10-09). */}
          <div className="settings-row">
            <span className="settings-row-label">Refresh</span>
            <button
              type="button"
              className="ui-btn ui-btn--primary"
              onClick={() => void handleRefreshNow()}
              disabled={refreshing}
            >
              {refreshing ? "Refreshing…" : "Refresh now"}
            </button>
            <span className="settings-row-hint">
              Re-pulls channels, VOD, series, and the guide, then tests the
              hidden channels again — all in the background, playback keeps running.
            </span>
          </div>
          <div className="settings-row">
            <label className="settings-row-label" htmlFor="hide-failing">
              Hide channels that keep failing
            </label>
            <input
              id="hide-failing"
              type="checkbox"
              checked={hideFailing}
              onChange={(e) => setHideFailing(e.target.checked)}
              aria-label="Hide channels that fail three separate tries"
            />
          </div>
          <p className="settings-row-hint">
            A channel that shows an error on three separate tries is hidden, never lost: it stays
            under Live TV's Hidden group and Channel status, and comes back when it answers again.
            Favorites are never hidden. Off shows them all.
          </p>
          <div className="settings-row">
            <span className="settings-row-label">Channel health</span>
            <button type="button" className="ui-btn" onClick={() => setStatusOpen(true)}>
              Channel status
            </button>
            <span className="settings-row-hint">
              {healthCounts.dead === 0 && healthCounts.degraded === 0
                ? "Which channels are failing, and why. Nothing is failing right now."
                : `Which channels are failing, and why — ${healthCounts.dead} not responding, ${healthCounts.degraded} failed a try.`}
            </span>
          </div>
        </div>

        <div className="settings-card">
          <h2 className="settings-card-title">Notifications</h2>
          <p className="settings-card-desc">
            Get alerted when a program on a favorited channel is about to
            start, and when a tracked title airs on any channel. Alerts are
            checked in the background every minute while this app is open.
          </p>
          <div className="settings-row">
            <span className="settings-row-label">Alerts</span>
            <button
              type="button"
              className={`ui-btn ${notifEnabled ? "ui-btn--primary" : ""}`}
              onClick={() => void handleToggleNotifications()}
              disabled={notifBusy}
              aria-pressed={notifEnabled}
            >
              {notifBusy ? "Requesting…" : notifEnabled ? "On" : "Off"}
            </button>
          </div>
          {browserPermission !== "granted" && browserPermission !== "unsupported" && (
            <div className="settings-row">
              <span className="settings-row-value settings-row-value--muted">
                Browser permission: {browserPermission}
              </span>
            </div>
          )}
          {notifHint && (
            <div className="settings-row">
              <span className="settings-row-hint">{notifHint}</span>
            </div>
          )}
          <div className="settings-row">
            <span className="settings-row-label">Tracked titles</span>
            {trackedTitles.length === 0 ? (
              <span className="settings-row-value settings-row-value--muted">
                None — tap “Track” on any movie, series, or program.
              </span>
            ) : (
              <ul className="settings-track-list">
                {trackedTitles.map((t) => (
                  <li key={t.title} className="settings-track-item">
                    <span className="settings-track-title">{t.title}</span>
                    <button
                      type="button"
                      className="ui-btn ui-btn--ghost"
                      onClick={() => removeTrackedTitle(t.title)}
                      aria-label={`Stop tracking ${t.title}`}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <PhoneRemoteCard />
        <YoutubeAccountCard />
        <EntitlementCard />
        <ThisBoxCard />
        <AppUpdateCard />
        <RemoteDebugCard />
      </div>

      {statusOpen && (
        <ChannelStatusDialog
          channels={statusChannels}
          health={channelHealth}
          onTryNow={retestChannelNow}
          onShowAnyway={(channel) => useChannelHealthStore.getState().showAnyway(channel.id)}
          onClose={() => setStatusOpen(false)}
        />
      )}
    </div>
  );
}
